import React, { useEffect, useRef, useState, useCallback } from "react";
import ContentEditable from "react-contenteditable";
import { IconButton } from "@mhome/ui";
import Tooltip from "@mui/material/Tooltip";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import StopIcon from "@mui/icons-material/Stop";
import { styled } from "@mui/material/styles";
import { Paper } from "@mhome/ui";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import { Typography } from "@mhome/ui";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  activeScopeState,
  promptOverridesState,
  selectedPromptIdState,
  selectedMentionState,
  inputModeState,
  wsReadyState,
  scopePreferenceState,
} from "../global-state";
import CloseIcon from "@mui/icons-material/Close";
import PromptDisplay from "./PromptDisplay";
import quickwordProcessor from "../common/quickword-processor";
import VoiceInputComponent from "./voice-input-component";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

// Constants
const maxContentLength = 20; // Maximum length for mention chip content before truncating

// Utility to strip HTML tags – gives us the plain text version of the editor content
const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

// Utility to insert an HTML string at the current caret position inside a content-editable element
const insertHtmlAtCaret = (html) => {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const el = document.createElement("div");
  el.innerHTML = html;
  const frag = document.createDocumentFragment();
  let node, lastNode;
  while ((node = el.firstChild)) {
    lastNode = frag.appendChild(node);
  }
  range.insertNode(frag);
  if (lastNode) {
    range.setStartAfter(lastNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
};

// Helper function to create mention chip HTML with entity tag inside
const createMentionChipHtml = (
  typeName,
  title,
  entityType,
  entityId,
  subtitle
) => {
  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");
  const mentionChipBg = isDark ? "#4F6B80" : "#E3F2FD";
  const mentionChipText = isDark ? "#BFDBFE" : "#1976D2";
  const mentionChipX = isDark ? "#94A3B8" : "#757575";
  const errorColor = "hsl(var(--destructive))";
  const isContentTruncated = title.length > maxContentLength;
  const truncatedTitle = isContentTruncated
    ? title.substring(0, maxContentLength - 3) + "..."
    : title;

  const displayContent = `@${typeName}:${truncatedTitle}`;

  // Create visual chip that contains an entity tag as its data representation
  return `<span contenteditable="false" class="mention-chip" ${
    isContentTruncated ? `title="@${typeName}:${title}"` : ""
  } style="display:inline-flex;align-items:center;padding:1px 4px 1px 6px;border-radius:16px;background:${mentionChipBg};color:${mentionChipText};font-size:14px;border:1px solid ${mentionChipBg};margin:0 1px;gap:4px;">${displayContent}<entity type="${entityType}" name="${title}" id="${entityId}" subtitle="${
    subtitle || ""
  }" typeName="${typeName}" style="display:none;"></entity><span class="chip-close" style="display:inline-flex;align-items:center;justify-content:center;width:12px;height:12px;border-radius:50%;background:${mentionChipX}40;color:${mentionChipX};cursor:pointer;font-size:10px;line-height:1;font-weight:bold;transition:all 0.2s;" onmouseover="this.style.background='${errorColor}';this.style.color='white';" onmouseout="this.style.background='${mentionChipX}40';this.style.color='${mentionChipX}';" onclick="event.stopPropagation();this.parentElement.remove();const editor=document.querySelector('[contenteditable]');if(editor){const event=new Event('input',{bubbles:true});editor.dispatchEvent(event);}">×</span></span>`;
};

const flexibleMatch = (textToSearch, query) => {
  if (!query) return true;
  const searchWords = query
    .toLowerCase()
    .split(" ")
    .filter((w) => w.length > 0);
  const targetText = textToSearch.toLowerCase();
  return searchWords.every((word) => targetText.includes(word));
};

const InstructionInputCE = ({
  placeholder,
  onSendMessage,
  onStop,
  isGenerating = false,
  dataFetchers,
}) => {
  /* ------------------------------------------------------------------ */
  /* --------------------------  State  -------------------------------- */
  /* ------------------------------------------------------------------ */
  const promptOverrides = useRecoilValue(promptOverridesState);
  const scopePreference = useRecoilValue(scopePreferenceState);
  const [selectedPromptId, setSelectedPromptId] = useRecoilState(
    selectedPromptIdState
  );
  const [selectedMention, setSelectedMention] =
    useRecoilState(selectedMentionState);
  const [promptsList, setPromptsList] = useState([]);
  const [activeScope] = useRecoilState(activeScopeState);
  const [html, setHtml] = useState("");
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [promptDisplay, setPromptDisplay] = useState(null);
  const prompt = useRef(null);
  const generating = useRef(isGenerating);

  // Voice recording state - using global state with localStorage persistence
  const [mode, setMode] = useRecoilState(inputModeState);

  // suggestions – the @ / # dropdown
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [mentionState, setMentionState] = useState(null); // null, 'types', 'entities', 'quickwords', 'prompts'
  const [currentMentionStart, setCurrentMentionStart] = useState(-1); // char offset where current @/# started
  const [currentEntityType, setCurrentEntityType] = useState("");
  const [currentEntityTypeName, setCurrentEntityTypeName] = useState("");
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [quickWordsList, setQuickWordsList] = useState([]);

  const contentEditableRef = useRef(null);
  const suggestionsRef = useRef(null);
  const selectedItemRef = useRef(null);
  const suggestionsShowingRef = useRef(false);

  // chat / ws plumbing re-used from legacy component (trimmed down)
  const wsReady = useRecoilValue(wsReadyState);
  const getComponentBgColor = useComponentBackgroundColor();

  // Misc
  const [isError, setIsError] = useState(false);

  // Combined error state that includes websocket connectivity
  const isInputDisabled = isError || !wsReady;

  useEffect(() => {
    if (selectedPromptId) {
      const selected = promptsList.find((p) => p.id === selectedPromptId);
      if (selected) {
        prompt.current = selected;
        setPromptDisplay(selected.title);
        setSelectedPromptId(null);
      }
    }
  }, [selectedPromptId, promptsList, setSelectedPromptId]);

  useEffect(() => {
    if (selectedMention) {
      // Insert mention chip directly into the editor
      const editor = contentEditableRef.current;
      if (editor) {
        editor.focus();

        // Get current selection
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) {
          // If no selection, create one at the end
          const range = document.createRange();
          range.selectNodeContents(editor);
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
        }

        const range = sel.getRangeAt(0);

        // Add space before mention if needed
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(editor);
        preCaretRange.setEnd(range.startContainer, range.startOffset);
        const textBeforeCaret = preCaretRange.toString();

        if (textBeforeCaret.length > 0 && !/\s$/.test(textBeforeCaret)) {
          const spaceNode = document.createTextNode(" ");
          range.insertNode(spaceNode);
          range.setStartAfter(spaceNode);
        }

        // Create general mention chip using the helper function
        const chipHtml = createMentionChipHtml(
          selectedMention.typeName,
          selectedMention.title,
          selectedMention.type,
          selectedMention.id,
          selectedMention.subtitle
        );

        // Create a temporary div to parse the HTML
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = chipHtml;
        const chipElement = tempDiv.firstChild;

        // Insert the chip
        range.insertNode(chipElement);

        // Create a text node with a space after the chip
        const spaceNode = document.createTextNode(" ");
        range.setStartAfter(chipElement);
        range.insertNode(spaceNode);

        // Position cursor after the space
        range.setStartAfter(spaceNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);

        // Update HTML state
        setHtml(contentEditableRef.current.innerHTML);
        setShowPlaceholder(false);
      }

      // Clear the selected mention
      setSelectedMention(null);
    }
  }, [selectedMention, setSelectedMention]);

  useEffect(() => {
    // Use promptOverrides directly as the full set of prompts
    setPromptsList(promptOverrides);

    if (prompt.current) {
      const newPrompt = promptOverrides.find((p) => p.id === prompt.current.id);
      if (newPrompt) {
        prompt.current = newPrompt;
        setPromptDisplay(newPrompt.title);
      }
    }
  }, [promptOverrides]);

  useEffect(() => {
    generating.current = isGenerating;
  }, [isGenerating]);

  // Sync suggestionsShowingRef with showSuggestions state
  useEffect(() => {
    suggestionsShowingRef.current = showSuggestions;
  }, [showSuggestions]);

  /* ------------------------------------------------------------------ */
  /* -----------------------  Derived values  -------------------------- */
  /* ------------------------------------------------------------------ */
  const isInputEmpty = stripHtml(html).trim().length === 0;

  const handleAudioRecorded = async (transcriptText) => {
    let instruction = transcriptText || "";
    if (prompt.current) {
      instruction = `${prompt.current.title}\n${instruction}`;
    }
    handleSendMessage(instruction, null, {});
    setHtml(""); // clear after send
    prompt.current = null;
    setPromptDisplay(null);
    setShowPlaceholder(true);
    setCurrentMentionStart(-1);
  };

  /* ------------------------------------------------------------------ */
  /* ---------------  Voice Recording Functions  ----------------------- */
  /* ------------------------------------------------------------------ */
  const handleVoiceResult = async (instruction) => {
    await handleSendMessage(instruction, null, {});
    // Clear after sending voice message
    setHtml("");
    prompt.current = null;
    setPromptDisplay(null);
    setShowPlaceholder(true);
    setCurrentMentionStart(-1);
  };

  const toggleMode = () => {
    setMode(mode === "text" ? "voice" : "text");
  };

  /* ------------------------------------------------------------------ */
  /* ----------------  Suggestions helpers (API)  ---------------------- */
  /* ------------------------------------------------------------------ */
  // Data fetching now handled by dataFetchers from wrapper
  const fetchEntityTypes = async () => {
    if (dataFetchers?.fetchEntityTypes) {
      return await dataFetchers.fetchEntityTypes();
    }
  };

  const fetchEntities = async (entityType) => {
    if (dataFetchers?.fetchEntities) {
      return await dataFetchers.fetchEntities(entityType);
    }
  };

  /* ------------------------------------------------------------------ */
  /* ---------------------  Mention detection  ------------------------- */
  /* ------------------------------------------------------------------ */
  const openTypeSuggestions = async (triggerPos) => {
    setLoadingSuggestions(true);
    const types = await fetchEntityTypes();
    setSuggestions(types);
    setMentionState("types");
    setCurrentMentionStart(triggerPos);
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
    setLoadingSuggestions(false);
  };

  const openQuickWordSuggestions = async (triggerPos) => {
    setLoadingSuggestions(true);
    try {
      let combinedQuickWords = [];

      if (dataFetchers?.fetchQuickWords) {
        // Use dataFetchers from wrapper
        combinedQuickWords = await dataFetchers.fetchQuickWords();
      }

      setQuickWordsList(combinedQuickWords);
      setSuggestions(combinedQuickWords);
      setMentionState("quickwords");
      setShowSuggestions(true);
      setSelectedSuggestionIndex(0);
      setCurrentMentionStart(triggerPos);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const openPromptSuggestions = async (triggerPos) => {
    setSuggestions(promptsList);
    setMentionState("prompts");
    setCurrentMentionStart(triggerPos);
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
  };

  /* ------------------------------------------------------------------ */
  /* --------------------  Content change handler  --------------------- */
  /* ------------------------------------------------------------------ */
  const handleContentChange = async (evt) => {
    const newHtml = evt.target.value;
    setHtml(newHtml);
    setShowPlaceholder(stripHtml(newHtml).trim() === "");

    // Check for @ / # trigger based on caret position & preceding char
    const cursorPos = getCaretCharacterOffset();
    const plain = stripHtml(newHtml);

    const triggerChar = plain[cursorPos - 1];
    const charBefore = cursorPos >= 2 ? plain[cursorPos - 2] : " ";

    // @ mention
    if (triggerChar === "@" && (cursorPos === 1 || charBefore === " ")) {
      await openTypeSuggestions(cursorPos - 1);
      return;
    }

    // # quick word (only if input empty besides # and no prompt selected)
    if (
      triggerChar === "#" &&
      (cursorPos === 1 || charBefore === " ") &&
      plain.substring(0, cursorPos - 1).trim() === "" &&
      !promptDisplay
    ) {
      await openQuickWordSuggestions(cursorPos - 1);
      return;
    }

    // / prompt
    if (triggerChar === "/" && (cursorPos === 1 || charBefore === " ")) {
      await openPromptSuggestions(cursorPos - 1);
      return;
    }

    // Dynamic filtering while suggestions are shown
    if (showSuggestions && currentMentionStart >= 0) {
      const filterText = plain
        .substring(currentMentionStart + 1, cursorPos)
        .toLowerCase();

      if (mentionState === "types") {
        // Re-fetch entity types and filter
        fetchEntityTypes().then((all) => {
          const filtered = all.filter((t) =>
            flexibleMatch(t.typeName, filterText)
          );
          setSuggestions(filtered);
          setSelectedSuggestionIndex((prev) =>
            Math.min(prev, Math.max(0, filtered.length - 1))
          );
        });
      } else if (mentionState === "entities") {
        const colonIndex = plain.indexOf(":", currentMentionStart);
        const query =
          colonIndex >= 0
            ? plain.substring(colonIndex + 1, cursorPos).toLowerCase()
            : "";
        // Re-fetch entities for current type and filter
        fetchEntities(currentEntityType).then((all) => {
          const filtered = all.filter((e) => flexibleMatch(e.title, query));
          setSuggestions(filtered);
          setSelectedSuggestionIndex((prev) =>
            Math.min(prev, Math.max(0, filtered.length - 1))
          );
        });
      } else if (mentionState === "quickwords") {
        const all = quickWordsList;
        const filtered = all.filter((w) => flexibleMatch(w.text, filterText));
        setSuggestions(filtered);
        setSelectedSuggestionIndex((prev) =>
          Math.min(prev, Math.max(0, filtered.length - 1))
        );
      } else if (mentionState === "prompts") {
        const all = promptsList;
        const filtered = all.filter((p) => flexibleMatch(p.title, filterText));
        setSuggestions(filtered);
        setSelectedSuggestionIndex((prev) =>
          Math.min(prev, Math.max(0, filtered.length - 1))
        );
      }

      // If trigger char was removed or caret is before it, close suggestions
      const triggerCharCurrent = plain[currentMentionStart];
      if (
        (triggerCharCurrent !== "@" &&
          triggerCharCurrent !== "#" &&
          triggerCharCurrent !== "/") ||
        cursorPos <= currentMentionStart
      ) {
        setShowSuggestions(false);
        setMentionState(null);
        setCurrentMentionStart(-1);
      }
    }
  };

  const handleMentionOrQuickWordIconClick = (char) => {
    const editor = contentEditableRef.current;
    if (!editor) return;

    editor.focus();

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) {
      // If no selection, create one at the end
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }

    const range = sel.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editor);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    const textBeforeCaret = preCaretRange.toString();

    let textToInsert = char;
    if (textBeforeCaret.length > 0 && !/\s$/.test(textBeforeCaret)) {
      textToInsert = " " + char;
    }

    // Insert text using Range API
    range.deleteContents();
    const textNode = document.createTextNode(textToInsert);
    range.insertNode(textNode);

    // Move caret after inserted text
    range.setStartAfter(textNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);

    // Dispatch 'input' event to trigger react-contenteditable's onChange
    const event = new Event("input", { bubbles: true });
    editor.dispatchEvent(event);
  };

  /* ------------------------------------------------------------------ */
  /* -------------------------  Key events  ---------------------------- */
  /* ------------------------------------------------------------------ */
  const handleKeyDown = useCallback(
    (event) => {
      // Handle backspace for chip deletion
      if (event.key === "Backspace") {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);

        // If there's a selection (not just cursor), let default behavior handle it
        if (!range.collapsed) return;

        // Check if cursor is right after a chip
        let nodeToCheck = range.startContainer;
        let offset = range.startOffset;

        // If we're in a text node and at the beginning, check previous sibling
        if (nodeToCheck.nodeType === Node.TEXT_NODE && offset === 0) {
          let prevSibling = nodeToCheck.previousSibling;
          if (
            prevSibling &&
            prevSibling.nodeType === Node.ELEMENT_NODE &&
            prevSibling.classList &&
            prevSibling.classList.contains("mention-chip")
          ) {
            event.preventDefault();
            prevSibling.remove();
            setHtml(contentEditableRef.current.innerHTML);
            return;
          }
        }

        // If we're directly in the contenteditable and cursor is after a chip
        if (nodeToCheck === contentEditableRef.current) {
          let prevChild = null;
          if (offset > 0) {
            prevChild = nodeToCheck.childNodes[offset - 1];
          }
          if (
            prevChild &&
            prevChild.nodeType === Node.ELEMENT_NODE &&
            prevChild.classList &&
            prevChild.classList.contains("mention-chip")
          ) {
            event.preventDefault();
            prevChild.remove();
            setHtml(contentEditableRef.current.innerHTML);
            return;
          }
        }

        // Also check if we're at the start of a text node that follows a chip
        if (nodeToCheck.nodeType === Node.TEXT_NODE) {
          const parent = nodeToCheck.parentNode;
          if (parent === contentEditableRef.current) {
            const nodeIndex = Array.from(parent.childNodes).indexOf(
              nodeToCheck
            );
            if (nodeIndex > 0 && offset === 0) {
              const prevNode = parent.childNodes[nodeIndex - 1];
              if (
                prevNode &&
                prevNode.nodeType === Node.ELEMENT_NODE &&
                prevNode.classList &&
                prevNode.classList.contains("mention-chip")
              ) {
                event.preventDefault();
                prevNode.remove();
                setHtml(contentEditableRef.current.innerHTML);
                return;
              }
            }
          }
        }
      }

      // Send on Enter when NO modifier keys and suggestions are not showing
      if (event.key === "Enter") {
        event.preventDefault();
        // Only send if suggestions are not showing
        if (!suggestionsShowingRef.current) {
          handleSend();
        }
      }
    },
    [showSuggestions, suggestions, selectedSuggestionIndex]
  );

  const getCaretCharacterOffset = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return 0;
    const range = sel.getRangeAt(0);
    const preRange = range.cloneRange();
    preRange.selectNodeContents(contentEditableRef.current);
    preRange.setEnd(range.endContainer, range.endOffset);
    return preRange.toString().length;
  };

  /* ------------------------------------------------------------------ */
  /* -------  Global key nav listener to improve reliability  ---------- */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!showSuggestions) return;

    const listener = (e) => {
      // Only react to keys we care about if suggestions are visible
      if (
        e.key === "ArrowDown" ||
        e.key === "ArrowUp" ||
        e.key === "Enter" ||
        e.key === "Escape" ||
        e.key === "Esc"
      ) {
        // Prevent browser default (scroll / caret move)
        e.preventDefault();
      }

      if (e.key === "ArrowDown") {
        setSelectedSuggestionIndex((prev) => {
          const next = prev < suggestions.length - 1 ? prev + 1 : 0;
          setTimeout(
            () => selectedItemRef.current?.scrollIntoView({ block: "nearest" }),
            0
          );
          return next;
        });
      }
      if (e.key === "ArrowUp") {
        setSelectedSuggestionIndex((prev) => {
          const next = prev > 0 ? prev - 1 : suggestions.length - 1;
          setTimeout(
            () => selectedItemRef.current?.scrollIntoView({ block: "nearest" }),
            0
          );
          return next;
        });
      }
      if (e.key === "Enter") {
        const sel = suggestions[selectedSuggestionIndex];
        if (sel) handleSuggestionSelect(sel);
      }
      if (e.key === "Escape" || e.key === "Esc") {
        setShowSuggestions(false);
        setMentionState(null);
        setCurrentMentionStart(-1);
      }
    };

    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, [showSuggestions, suggestions, selectedSuggestionIndex, mentionState]);

  /* ------------------------------------------------------------------ */
  /* --------  Click outside listener to close suggestions  ------------ */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!showSuggestions) return;

    const handleClickOutside = (event) => {
      // Check if click is outside both the suggestions dropdown and the content editable area
      const clickedInsideSuggestions = suggestionsRef.current?.contains(
        event.target
      );
      const clickedInsideEditor = contentEditableRef.current?.contains(
        event.target
      );

      if (!clickedInsideSuggestions && !clickedInsideEditor) {
        setShowSuggestions(false);
        setMentionState(null);
        setCurrentMentionStart(-1);
      }
    };

    const handleTouchOutside = (event) => {
      // Same logic for touch events on mobile, but with a small delay to allow suggestion selection
      const clickedInsideSuggestions = suggestionsRef.current?.contains(
        event.target
      );
      const clickedInsideEditor = contentEditableRef.current?.contains(
        event.target
      );

      if (!clickedInsideSuggestions && !clickedInsideEditor) {
        // Small delay to prevent interference with suggestion selection on touch devices
        setTimeout(() => {
          setShowSuggestions(false);
          setMentionState(null);
          setCurrentMentionStart(-1);
        }, 100);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleTouchOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleTouchOutside);
    };
  }, [showSuggestions]);

  /* ------------------------------------------------------------------ */
  /* -----------  Chip close button click handler  --------------------- */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const handleChipClose = (event) => {
      // Check if clicked element is a chip close button
      if (event.target.classList.contains("chip-close")) {
        event.stopPropagation();
        event.preventDefault();

        // Find and remove the parent chip
        const chip = event.target.closest(".mention-chip");
        if (chip) {
          chip.remove();
          // Update the HTML state to reflect the change
          setHtml(contentEditableRef.current.innerHTML);
          // Focus back to editor
          contentEditableRef.current?.focus();
        }
      }
    };

    // Add event listener to the contentEditable element
    const editor = contentEditableRef.current;
    if (editor) {
      editor.addEventListener("click", handleChipClose);
      return () => editor.removeEventListener("click", handleChipClose);
    }
  }, []);

  /* ------------------------------------------------------------------ */
  /* -----------------------  Suggestion select  ----------------------- */
  /* ------------------------------------------------------------------ */
  const handleSuggestionSelect = async (suggestion) => {
    console.debug("handleSuggestionSelect called", {
      suggestion,
      mentionState,
      currentMentionStart,
    });

    // helper: remove text between currentMentionStart and caret
    const replaceCurrentMentionRange = () => {
      const start = currentMentionStart;
      const end = getCaretCharacterOffset();
      if (start < 0 || end <= start) return;
      const container = contentEditableRef.current;

      // For iPhone compatibility, use a more robust approach
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isIOS) {
        // iPhone-specific approach: manipulate HTML directly
        const currentHtml = container.innerHTML;
        const plainText = stripHtml(currentHtml);
        const beforeText = plainText.substring(0, start);
        const afterText = plainText.substring(end);
        const newText = beforeText + afterText;

        // Create new HTML with preserved formatting
        const tempDiv = document.createElement("div");
        tempDiv.textContent = newText;
        container.innerHTML = tempDiv.innerHTML;

        // Position cursor at the start position
        setTimeout(() => {
          const sel = window.getSelection();
          if (sel && container.firstChild) {
            const range = document.createRange();
            const textNode = container.firstChild;
            const cursorPos = Math.min(start, textNode.textContent.length);
            range.setStart(textNode, cursorPos);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }, 0);
      } else {
        // Original approach for other browsers
        const walker = document.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );
        let currentOffset = 0;
        let startNode = null,
          endNode = null,
          startOffsetInNode = 0,
          endOffsetInNode = 0;
        while (walker.nextNode()) {
          const node = walker.currentNode;
          const len = node.textContent.length;
          if (startNode === null && currentOffset + len >= start) {
            startNode = node;
            startOffsetInNode = start - currentOffset;
          }
          if (currentOffset + len >= end) {
            endNode = node;
            endOffsetInNode = end - currentOffset;
            break;
          }
          currentOffset += len;
        }
        if (startNode && endNode) {
          const range = document.createRange();
          range.setStart(startNode, startOffsetInNode);
          range.setEnd(endNode, endOffsetInNode);
          range.deleteContents();
        }
      }
    };

    if (mentionState === "prompts") {
      console.debug("Processing prompt selection", {
        suggestion,
        currentMentionStart,
      });
      prompt.current = suggestion;
      setPromptDisplay(suggestion.title);

      // Only remove the prompt trigger text, preserve existing content
      replaceCurrentMentionRange();
      setHtml(contentEditableRef.current.innerHTML);

      // Update placeholder visibility based on remaining content
      const remainingText = stripHtml(
        contentEditableRef.current.innerHTML
      ).trim();
      setShowPlaceholder(remainingText === "");

      setShowSuggestions(false);
      setMentionState(null);
      setCurrentMentionStart(-1);

      // Focus and position cursor at the end
      setTimeout(() => {
        contentEditableRef.current?.focus();
      }, 0);
      return;
    }

    if (mentionState === "types") {
      // user picked an entity type – switch to entities list
      setLoadingSuggestions(true);
      const entities = await fetchEntities(suggestion.type);
      setSuggestions(entities);
      setMentionState("entities");
      setCurrentEntityType(suggestion.type);
      setCurrentEntityTypeName(suggestion.typeName);
      setSelectedSuggestionIndex(0);
      setLoadingSuggestions(false);

      // replace typed token with @typeName:
      replaceCurrentMentionRange();
      insertHtmlAtCaret(`@${suggestion.typeName}:`);
      setHtml(contentEditableRef.current.innerHTML);

      // keep suggestions open for entities selection – caret is already after colon
      return;
    }

    if (mentionState === "entities") {
      // remove entire mention (prefix + entity chars)
      replaceCurrentMentionRange();

      // Get current selection for cursor positioning
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);

      // Create chip element using the helper function
      const chipHtml = createMentionChipHtml(
        currentEntityTypeName,
        suggestion.title,
        currentEntityType,
        suggestion.id,
        suggestion.subtitle
      );

      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = chipHtml;
      const chipElement = tempDiv.firstChild;

      // Insert the chip
      range.insertNode(chipElement);

      // Create a text node with a space after the chip
      const spaceNode = document.createTextNode(" ");
      range.setStartAfter(chipElement);
      range.insertNode(spaceNode);

      // Position cursor after the space
      range.setStartAfter(spaceNode);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);

      // Update HTML state
      setHtml(contentEditableRef.current.innerHTML);

      // close dropdown
      setShowSuggestions(false);
      setMentionState(null);
      setCurrentMentionStart(-1);
      return;
    }

    if (mentionState === "quickwords") {
      replaceCurrentMentionRange();

      // Get current selection for cursor positioning
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);

      // Helper function to insert text and update cursor
      const insertTextAndUpdateCursor = (text) => {
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);

        // Position cursor after the inserted text
        range.setStartAfter(textNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);

        // Update HTML state
        setHtml(contentEditableRef.current.innerHTML);
      };

      // Process quickword using the processor
      const quickwordText = suggestion.text;

      // Check if we can process synchronously
      if (quickwordProcessor.canProcessSync(quickwordText)) {
        // Synchronous processing
        const processedText =
          quickwordProcessor.processQuickwordSync(quickwordText);
        insertTextAndUpdateCursor(processedText);
      } else {
        // Async processing needed for temperature variables
        quickwordProcessor
          .processQuickword(quickwordText, scopePreference[activeScope])
          .then((processedText) => {
            insertTextAndUpdateCursor(processedText);
          })
          .catch((error) => {
            console.error("Error processing quickword:", error);
            insertTextAndUpdateCursor(quickwordText); // Fallback to original text
          });
      }

      setShowSuggestions(false);
      setMentionState(null);
      setCurrentMentionStart(-1);
      return;
    }
  };

  /* ------------------------------------------------------------------ */
  /* --------------------------  Send logic  --------------------------- */
  /* ------------------------------------------------------------------ */
  const parseEditorContent = (htmlContent) => {
    const container = document.createElement("div");
    container.innerHTML = htmlContent || html;

    // Process nodes to extract text content while preserving entity tags from chips
    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node;

        // Handle mention chips - extract the hidden entity tag
        if (el.classList && el.classList.contains("mention-chip")) {
          const entityTag = el.querySelector("entity");
          if (entityTag) {
            const type = entityTag.getAttribute("type") || "";
            const name = entityTag.getAttribute("name") || "";
            const id = entityTag.getAttribute("id") || "";
            const subtitle = entityTag.getAttribute("subtitle") || "";
            const typeName = entityTag.getAttribute("typeName") || "";
            return `<entity type="${type}" name="${name}" id="${id}" subtitle="${subtitle}" typeName="${typeName}"></entity>`;
          }
          return ""; // Fallback if no entity tag found
        }

        // Handle standalone entity tags (shouldn't normally occur with chips, but just in case)
        if (el.tagName && el.tagName.toLowerCase() === "entity") {
          const type = el.getAttribute("type") || "";
          const name = el.getAttribute("name") || "";
          const id = el.getAttribute("id") || "";
          const subtitle = el.getAttribute("subtitle") || "";
          const typeName = el.getAttribute("typeName") || "";
          return `<entity type="${type}" name="${name}" id="${id}" subtitle="${subtitle}" typeName="${typeName}"></entity>`;
        }

        // For other elements, process their children
        let content = "";
        for (const child of el.childNodes) {
          content += processNode(child);
        }
        return content;
      }
      return "";
    };

    let instruction = "";
    for (const child of container.childNodes) {
      instruction += processNode(child);
    }

    return { instruction: instruction.trim() };
  };

  const handleSend = async () => {
    // If currently generating, call onStop instead of sending
    if (generating.current) {
      if (onStop) {
        onStop();
      }
      return;
    }

    // Get current HTML content directly from the contentEditable element
    const currentHtml = contentEditableRef.current?.innerHTML || "";
    let { instruction } = parseEditorContent(currentHtml);

    // Check if the parsed instruction is empty
    if (!instruction) return;
    if (prompt.current) {
      instruction = `${prompt.current.title}\n${instruction}`;
    }

    handleSendMessage(instruction, null, {});
    setHtml(""); // clear after send
    prompt.current = null;
    setPromptDisplay(null);
    setShowPlaceholder(true);
    setCurrentMentionStart(-1);
  };

  const handleSendMessage = async (instruction, audioFile, mentions) => {
    if (onSendMessage) {
      await onSendMessage(instruction, audioFile, mentions);
    }
  };

  /* ------------------------------------------------------------------ */
  /* ---------------------------  Render  ------------------------------ */
  /* ------------------------------------------------------------------ */
  return (
    <div style={{ background: "transparent" }}>
      {/* top padding placeholder now handled by wrapper */}

      {/* Main editor area */}
      <div
        style={{
          borderRadius: "16px",
          backgroundColor: getComponentBgColor("hsl(var(--background))"),
        }}
      >
        {promptDisplay && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "8px 12px",
              borderBottom: `1px solid ${"hsl(var(--border))"}`,
              gap: "8px",
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
            }}
          >
            <Typography
              variant="body2"
              style={{
                fontWeight: 500,
                flexGrow: 1,
                color: "hsl(var(--muted-foreground))",
              }}
              component="div"
            >
              <PromptDisplay text={promptDisplay} />
            </Typography>
            <IconButton
              onClick={() => {
                prompt.current = null;
                setPromptDisplay(null);
              }}
              size="small"
              style={{ padding: "2px" }}
            >
              <CloseIcon style={{ fontSize: "16px" }} />
            </IconButton>
          </div>
        )}
        <div
          style={{
            paddingTop: "4px",
            paddingBottom: "4px",
            paddingLeft: "12px",
            paddingRight: "12px",
          }}
        >
          {mode === "text" ? (
            <div style={{ marginBottom: "16px", position: "relative" }}>
              <ContentEditable
                innerRef={contentEditableRef}
                html={html}
                disabled={isInputDisabled}
                onChange={handleContentChange}
                onKeyDown={handleKeyDown}
                onKeyUp={() => {}}
                tagName="div"
                style={{
                  minHeight: "40px",
                  maxHeight: "200px",
                  outline: "none",
                  whiteSpace: "pre-wrap",
                  fontSize: "16px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  wordWrap: "break-word",
                  wordBreak: "break-word",
                }}
              />
              {showPlaceholder && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    color: "hsl(var(--muted-foreground))",
                    pointerEvents: "none",
                    fontSize: "16px",
                    padding: "2px 0",
                  }}
                >
                  {isGenerating
                    ? "Generating..."
                    : isInputDisabled
                    ? !wsReady
                      ? "Connecting..."
                      : "Assistant error. Please start a new chat."
                    : placeholder}
                </div>
              )}

              {/* Suggestions dropdown */}
              {showSuggestions &&
                (suggestions.length > 0 || loadingSuggestions) && (
                  <Paper
                    ref={suggestionsRef}
                    elevation={0}
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      maxHeight: "200px",
                      overflow: "auto",
                      borderRadius: "8px",
                      marginBottom: "8px",
                      boxShadow: "none",
                      // iPhone-specific touch improvements
                      WebkitOverflowScrolling: "touch",
                      touchAction: "manipulation",
                    }}
                  >
                    {loadingSuggestions ? (
                      <div style={{ padding: "8px", textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          style={{ color: "hsl(var(--muted-foreground))" }}
                        >
                          Loading...
                        </Typography>
                      </div>
                    ) : (
                      <List dense>
                        {suggestions.map((suggestion, index) => (
                          <ListItem key={index} disablePadding sx={{ py: 0 }}>
                            <ListItemButton
                              ref={
                                index === selectedSuggestionIndex
                                  ? selectedItemRef
                                  : null
                              }
                              selected={index === selectedSuggestionIndex}
                              onClick={() => handleSuggestionSelect(suggestion)}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.debug(
                                  "TouchEnd on suggestion",
                                  suggestion
                                );
                                handleSuggestionSelect(suggestion);
                              }}
                              onTouchStart={(e) => {
                                e.stopPropagation();
                              }}
                              sx={{
                                py: 0.5,
                                px: 1,
                                minHeight: "32px",
                                cursor: "pointer",
                                touchAction: "manipulation",
                                WebkitTapHighlightColor: "transparent",
                                "&.Mui-selected": {
                                  backgroundColor: "primary.main",
                                  color: "primary.contrastText",
                                  "&:hover": {
                                    backgroundColor: "primary.dark",
                                  },
                                },
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "16px", lineHeight: 1.4 }}
                                component={
                                  mentionState === "prompts" ||
                                  mentionState === "quickwords"
                                    ? "div"
                                    : "p"
                                }
                              >
                                {mentionState === "types" ? (
                                  `@${suggestion.typeName}:`
                                ) : mentionState === "quickwords" ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "6px",
                                        height: "6px",
                                        borderRadius: "50%",
                                        // Green dot for regular quick words, orange dot for suggested quick words
                                        backgroundColor:
                                          suggestion.type === "regular"
                                            ? "hsl(var(--green-500))"
                                            : "hsl(var(--yellow-500))",
                                        flexShrink: 0,
                                      }}
                                    />
                                    {suggestion.text}
                                  </div>
                                ) : mentionState === "prompts" ? (
                                  <PromptDisplay text={suggestion.title} />
                                ) : (
                                  suggestion.title || suggestion
                                )}
                              </Typography>
                              {mentionState === "entities" &&
                                suggestion.subtitle && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "12px",
                                      color: "text.secondary",
                                      lineHeight: 1.2,
                                      display: "block",
                                      marginLeft: 0.5,
                                    }}
                                  >
                                    {suggestion.subtitle}
                                  </Typography>
                                )}
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Paper>
                )}
            </div>
          ) : (
            <></>
          )}

          {/* Bottom controls */}
          {mode === "text" ? (
            /* Text mode controls */
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Tooltip title="Quick Commands">
                  <span>
                    <IconButton
                      onClick={() => handleMentionOrQuickWordIconClick("#")}
                      sx={{
                        width: "32px",
                        height: "32px",
                        padding: 0,
                        borderRadius: "8px",
                        bgcolor: "action.hover",
                        "&:hover": { bgcolor: "action.selected" },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      disabled={
                        isInputDisabled || !isInputEmpty || !!promptDisplay
                      }
                      size="small"
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color:
                            isInputDisabled || !isInputEmpty || !!promptDisplay
                              ? "action.disabled"
                              : "text.secondary",
                          lineHeight: 1,
                        }}
                      >
                        #
                      </Typography>
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Prompts">
                  <span>
                    <IconButton
                      onClick={() => handleMentionOrQuickWordIconClick("/")}
                      sx={{
                        width: "32px",
                        height: "32px",
                        padding: 0,
                        borderRadius: "8px",
                        bgcolor: "action.hover",
                        "&:hover": { bgcolor: "action.selected" },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      disabled={isInputDisabled}
                      size="small"
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: isInputDisabled
                            ? "action.disabled"
                            : "text.secondary",
                          lineHeight: 1,
                        }}
                      >
                        /
                      </Typography>
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Mention Entity">
                  <span>
                    <IconButton
                      onClick={() => handleMentionOrQuickWordIconClick("@")}
                      sx={{
                        width: "32px",
                        height: "32px",
                        padding: 0,
                        borderRadius: "8px",
                        bgcolor: "action.hover",
                        "&:hover": { bgcolor: "action.selected" },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      disabled={isInputDisabled}
                      size="small"
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: isInputDisabled
                            ? "action.disabled"
                            : "text.secondary",
                          lineHeight: 1,
                        }}
                      >
                        @
                      </Typography>
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
              <div
                style={{
                  alignSelf: "flex-end",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Tooltip title="Voice Input">
                  <span>
                    <VoiceInputComponent
                      mode={mode}
                      onModeToggle={toggleMode}
                      onVoiceResult={handleVoiceResult}
                      isInputDisabled={isInputDisabled}
                      variant="default"
                      prompt={prompt.current}
                      showModeButton={true}
                      isGenerating={isGenerating}
                      onStop={onStop}
                    />
                  </span>
                </Tooltip>
                <Tooltip title={isGenerating ? "Stop" : "Send"}>
                  <span>
                    <CustomSendButton
                      onClick={handleSend}
                      disabled={
                        isInputDisabled || (!isGenerating && isInputEmpty)
                      }
                    >
                      {isGenerating ? <StopIcon /> : <KeyboardArrowUpIcon />}
                    </CustomSendButton>
                  </span>
                </Tooltip>
              </div>
            </div>
          ) : (
            /* Voice mode controls */
            <VoiceInputComponent
              mode={mode}
              onModeToggle={toggleMode}
              onVoiceResult={handleVoiceResult}
              isInputDisabled={isInputDisabled}
              variant="default"
              prompt={prompt.current}
              showModeButton={true}
              isGenerating={isGenerating}
              onStop={onStop}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* ---------------------------  Styles  ------------------------------ */
/* ------------------------------------------------------------------ */
const CustomSendButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "hsl(var(--primary))",
  color: "hsl(var(--primary-foreground))",
  borderRadius: "8px",
  padding: "10px",
  width: "32px",
  height: "32px",
  "&:hover": {
    backgroundColor: "hsl(var(--primary) / 0.8)",
  },
  "&:disabled": {
    backgroundColor: "hsl(var(--muted))",
    color: "hsl(var(--muted-foreground))",
  },
}));

export default InstructionInputCE;
